import { useState, useEffect, useCallback } from 'react';
import { useAuth } from './useAuth';
import { astrologyService, AstrologyData } from '../services/astrologyApi';
import { userService } from '../services/api';

export type AstralInfo = AstrologyData;

const FALLBACK: AstralInfo = {
  sign: 'Leão',
  element: 'Fogo',
  planet: 'Sol',
  dailyMessage: 'Sua criatividade está em alta hoje. É um ótimo momento para expressar sua individualidade.',
  compatibility: ['Áries', 'Sagitário', 'Gêmeos', 'Libra'],
  luckyNumbers: [7, 14, 21, 28, 35, 42],
  luckyColors: ['Dourado'],
  moonPhase: astrologyService.getCurrentMoonPhase(),
  planetaryInfluences: astrologyService.getPlanetaryInfluences(),
};

/**
 * Carrega (com cache diário em localStorage) as informações astrais do usuário:
 * signo, elemento, numerologia pessoal, cor do dia, mapa astral etc.
 * Compartilhado entre Dashboard e Perfil para não duplicar a lógica de fetch.
 */
export function useAstralInfo() {
  const { user } = useAuth();
  const [astralInfo, setAstralInfo] = useState<AstralInfo | null>(null);
  const [birthChart, setBirthChart] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const today = new Date().toDateString();
      const me = await userService.getMe();
      const profile = me.profile;

      // Inclui birth_date na chave para invalidar cache quando o perfil mudar
      const profileVersion = profile?.birth_date || 'no-profile';
      const keyData = `astralInfo:${user?.id || 'anon'}:${profileVersion}`;
      const keyDate = `astralInfoDate:${user?.id || 'anon'}:${profileVersion}`;
      const storedDate = localStorage.getItem(keyDate);
      const storedData = localStorage.getItem(keyData);

      if (storedDate === today && storedData) {
        setAstralInfo(JSON.parse(storedData));
        return;
      }

      if (!profile || !profile.birth_date) {
        setAstralInfo(FALLBACK);
        localStorage.setItem(keyData, JSON.stringify(FALLBACK));
        localStorage.setItem(keyDate, today);
        return;
      }

      const birthDate = new Date(profile.birth_date + 'T00:00:00');
      const birthTime = profile.birth_time ? new Date(`1970-01-01T${profile.birth_time}`) : undefined;
      let chart = profile.birth_chart_data;
      if (!chart && profile.birth_time && profile.birth_latitude && profile.birth_longitude) {
        try { chart = await userService.generateBirthChart(); } catch {}
      }
      if (chart) setBirthChart(chart);
      const astralData = await astrologyService.getAstrologyDataFromProfile({
        birth_date: birthDate,
        birth_time: birthTime,
        birth_latitude: profile.birth_latitude,
        birth_longitude: profile.birth_longitude,
        zodiac_sign: profile.zodiac_sign,
        name: me.name,
      }, chart);
      setAstralInfo(astralData);

      localStorage.setItem(keyData, JSON.stringify(astralData));
      localStorage.setItem(keyDate, today);
    } catch (error) {
      console.error('Erro ao carregar informações astrais:', error);
      setAstralInfo(FALLBACK);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => { load(); }, [load]);

  return { astralInfo, birthChart, loading, reload: load };
}
